import { flexRender, type Table } from '@tanstack/react-table';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';

type DataTableProps<T> = {
  /** A `@tanstack/react-table` instance owned by the caller. */
  table: Table<T>;
  /**
   * Per-column responsive visibility/alignment classes keyed by column id,
   * applied to both the header cell and the body cell so they stay in sync.
   */
  columnClass?: Record<string, string>;
  /** Called with the row's original data when a row is clicked. */
  onRowClick?: (row: T) => void;
  /** Determines whether an individual row can invoke `onRowClick`. */
  isRowClickable?: (row: T) => boolean;
};

/**
 * Generic presentational table shell shared across the trade and history pages.
 * Callers own their column defs, row data, and table state via their own
 * `useReactTable` instance and pass it in here for rendering. Row selection
 * (when the table enables it) drives the selected-row highlight.
 */
export function DataTable<T>({
  table,
  columnClass = {},
  onRowClick,
  isRowClickable,
}: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead
          className={classNames(
            'border-y-1 border-token-gray-300',
            fontStyles({ variant: 'LABEL_SM', class: 'text-token-gray-800' }),
          )}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="group/header-row text-left">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={classNames(
                    'align-middle px-3 py-3.5 first:pl-7 last:pr-7',
                    columnClass[header.column.id],
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const rowIsClickable =
              !!onRowClick && (isRowClickable ? isRowClickable(row.original) : true);

            return (
              <tr
                key={row.id}
                onClick={rowIsClickable && onRowClick ? () => onRowClick(row.original) : undefined}
                aria-selected={row.getIsSelected() || undefined}
                className={classNames(
                  fontStyles({ variant: 'BODY_MD' }),
                  'group/row border-b-1 border-token-gray-300 h-12 transition-colors duration-150',
                  rowIsClickable ? 'cursor-pointer' : undefined,
                  'hover:bg-token-gray-200 active:bg-token-gray-200 last:border-b-0',
                  'aria-selected:bg-[rgba(89,50,243,0.08)] aria-selected:hover:bg-[rgba(89,50,243,0.08)]',
                  'last:[&>td:first-child]:rounded-bl-2xl last:[&>td:last-child]:rounded-br-2xl last:rounded-b-2xl',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={classNames(
                      'align-middle px-3 py-3 first:pl-7 last:pr-7',
                      columnClass[cell.column.id],
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
